import { useMemo, useState } from 'react';
import { CustomDatePicker } from '../../../common/datepicker/types';
import { filterPrompts } from './rightPanelHelpers';
import { humanizeDate } from '../../../helpers/humanizeDate';
import { PromptExpandableContent } from './PromptExpandableContent';
import { PromptWithCategory } from '../types';
import { PromptRowActionsMenu } from './PromptRowActionsMenu';
import { ACTION_BUTTONS_KEY } from '../../../common/table/utils';
import { PromptAuthor } from './PromptAuthor';
import { isSweepDefaultPrompt } from '@server/ai-prompts.config';

interface UsePromptsTableParams {
  prompts: PromptWithCategory[];
  selectedCategoryFilters?: string[];
  selectedUserFilters?: string[];
  onSelectedUserFiltersChange?: (filters: string[]) => void;
  onEditPrompt: (prompt: PromptWithCategory) => void;
  onDeletePrompt: ({ groupId, promptId }: { groupId: string; promptId: string }) => void;
  onUseInChatSuccess: () => void;
}

export const usePromptsTable = ({
  prompts,
  selectedCategoryFilters = [],
  selectedUserFilters = [],
  onEditPrompt,
  onUseInChatSuccess,
  onDeletePrompt,
}: UsePromptsTableParams) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState<CustomDatePicker | null>(null);
  const [expandedRowId, setExpandedRowId] = useState<string>();

  const filteredPrompts = useMemo(
    () =>
      filterPrompts({
        prompts,
        searchTerm,
        selectedCategoryFilters,
        selectedUserFilters,
        dateFilter,
      }),
    [prompts, searchTerm, selectedCategoryFilters, selectedUserFilters, dateFilter],
  );

  const rows: {
    updatedAt: React.ReactNode;
    updatedByName: React.ReactNode;
    expandableContent: React.ReactNode;
    id: string;
    name: string;
    categoryName?: string;
    [ACTION_BUTTONS_KEY]: React.ReactNode;
  }[] = useMemo(
    () =>
      filteredPrompts.map((prompt) => {
        const isSweepPrompt = isSweepDefaultPrompt(
          prompt.name,
          prompt.categoryName,
          prompt.updatedById,
        );
        return {
          ...prompt,
          id: prompt.id,
          updatedAt: <>{humanizeDate({ dateOrTimestamp: prompt.updatedAt })}</>,
          updatedByName: prompt.updatedById ? (
            <PromptAuthor userId={prompt.updatedById} />
          ) : isSweepPrompt ? (
            <PromptAuthor isSweep />
          ) : (
            '-'
          ),
          expandableContent: (
            <PromptExpandableContent
              prompt={prompt}
              onTestAndEdit={() => onEditPrompt?.(prompt)}
              onUseInChatSuccess={onUseInChatSuccess}
              onDelete={onDeletePrompt}
            />
          ),
          [ACTION_BUTTONS_KEY]: (
            <PromptRowActionsMenu
              prompt={prompt}
              onEdit={onEditPrompt}
              onDelete={onDeletePrompt}
              onUseInChatSuccess={onUseInChatSuccess}
            />
          ),
        };
      }),
    [filteredPrompts, onEditPrompt, onUseInChatSuccess, onDeletePrompt],
  );

  return {
    searchTerm,
    setSearchTerm,
    dateFilter,
    setDateFilter,
    expandedRowId,
    setExpandedRowId,
    filteredPrompts,
    rows,
  };
};
